import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TextField, SelectField } from "@/components/poshan/FormBits";
import { OdiaMdmReport, OdiaMdmReportSimple, EngMdcfReport } from "@/components/poshan/MdcfReports";
import { Printer, FileDown, Sparkles, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useActiveSchool } from "@/lib/activeSchool";
import { dailyApi, cchApi, fundApi, enrollmentApi, financialYearFromMonth } from "@/lib/setupApi";
import {
  defaultMdcfData, autoFillMdcf, loadMdcf, saveMdcf, MONTH_OPTIONS,
} from "@/lib/mdcf";
import { mdcfApi } from "@/lib/setupApi";
import MobileMonthlyMDCF from "@/components/poshan/MobileMonthlyMDCF";
import { useDailyEntriesVersion } from "@/lib/dailyEntriesBus";
import { useMasterDataVersion } from "@/lib/masterDataBus";
import { useRateCardsSync, refreshRateCards } from "@/lib/useRateCardsSync";

function todayParts() {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1 };
}

export default function MonthlyMDCF() {
  useRateCardsSync();
  const { active, profiles } = useActiveSchool();
  const schoolId = active?.id || "";
  // iter147 — auto-refresh whenever a Daily Entry is saved anywhere in the app.
  const dailyEntriesVersion = useDailyEntriesVersion();
  // iter149 — also auto-refresh on Vendor/CCH edits and Rate-Card changes.
  const { vendorsV, cchsV } = useMasterDataVersion();
  // iter149 — recompute the MDCF whenever rate cards refresh so Section 10
  // (ଚାଉଳ ବ୍ୟତୀତ ଅନ୍ୟାନ୍ୟ ସାମଗ୍ରୀ) and other rate-driven columns pick up
  // admin's latest qty/rate values.
  const [rateCardTick, setRateCardTick] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      await refreshRateCards();
      if (alive) setRateCardTick((n) => n + 1);
    })();
    return () => { alive = false; };
  }, [dailyEntriesVersion, vendorsV, cchsV]);
  const [{ y: curYear, m: curMonth }] = useState(todayParts());
  const [year, setYear] = useState(curYear);
  const [month, setMonth] = useState(curMonth);
  const [tab, setTab] = useState("odia");
  const [data, setData] = useState(() => defaultMdcfData());
  const [dailyEntries, setDailyEntries] = useState([]);
  const [cchs, setCchs] = useState([]);
  const [fundBalance, setFundBalance] = useState(null);
  // Cascade source: previous month's fund balance + previous month's classwise consumed
  const [prevFundBalance, setPrevFundBalance] = useState(null);
  const [prevConsumed, setPrevConsumed] = useState(null);

  // Year-wise enrollment snapshot for the FY of the currently-selected month.
  // When a record exists, autoFillMdcf overrides school-profile enrollment with
  // these numbers — so a school can record its actual headcount each FY without
  // re-typing on every monthly bill.
  const [enrollmentRow, setEnrollmentRow] = useState(null);
  const fy = useMemo(() => financialYearFromMonth(year, month), [year, month]);
  useEffect(() => {
    if (!schoolId) { setEnrollmentRow(null); return; }
    let cancelled = false;
    enrollmentApi.get(schoolId, fy)
      .then((d) => { if (!cancelled) setEnrollmentRow(d || null); })
      .catch(() => { if (!cancelled) setEnrollmentRow(null); });
    return () => { cancelled = true; };
  }, [schoolId, fy]);

  // Build a "school" object that carries the FY-snapshot enrollment counts so
  // they win over the static school-profile values inside autoFillMdcf.
  const activeWithFy = useMemo(() => {
    if (!active) return active;
    if (!enrollmentRow) return active;
    return {
      ...active,
      balVatikaEnrollment: enrollmentRow.balVatika ?? active.balVatikaEnrollment,
      primaryEnrollment: enrollmentRow.primary ?? active.primaryEnrollment,
      upperPrimaryEnrollment: enrollmentRow.upperPrimary ?? active.upperPrimaryEnrollment,
      highSchoolEnrollment: enrollmentRow.highSchool ?? active.highSchoolEnrollment,
    };
  }, [active, enrollmentRow]);

  const yearChoices = useMemo(() => [curYear - 2, curYear - 1, curYear, curYear + 1], [curYear]);

  // Load CCH master once
  useEffect(() => {
    cchApi.list().then(setCchs).catch(() => setCchs([]));
  }, []);

  // Load fund balance for current school+year+month
  useEffect(() => {
    if (!schoolId) { setFundBalance(null); return; }
    let cancelled = false;
    fundApi.get(schoolId, year, month)
      .then((d) => { if (!cancelled) setFundBalance(d); })
      .catch(() => { if (!cancelled) setFundBalance(null); });
    return () => { cancelled = true; };
  }, [schoolId, year, month]);

  // ----- CASCADE: load previous month's TRANSITIVE closing -----
  // The closing balance of month N (per class) becomes the opening balance of
  // month N+1. We walk back recursively through every saved fund record so the
  // cascade is correct even when the chain is many months long. Shared helper
  // lives in /lib/fundCascade.js (same logic powers Fund Management UI).
  useEffect(() => {
    if (!schoolId) { setPrevFundBalance(null); setPrevConsumed(null); return; }
    let cancelled = false;
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    (async () => {
      try {
        const { cascadeOpening } = await import("@/lib/fundCascade");
        const empty = () => ({ balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 });
        const prevClosing = await cascadeOpening(schoolId, year, month);
        if (cancelled) return;
        if (prevClosing) {
          // Synthesize a prevFundBalance whose stored opening is the cascaded
          // closing — the existing autoFillMdcf math `(opening + received - consumed)`
          // returns exactly that closing as the opening of the current month.
          setPrevFundBalance({
            openingFund: prevClosing.fund,
            receivedFund: empty(),
            openingRice: prevClosing.rice,
            receivedRice: empty(),
          });
          setPrevConsumed({ cost: empty(), rice: empty() });
        } else {
          setPrevFundBalance(null);
          setPrevConsumed(null);
        }
      } catch (err) {
        if (!cancelled) { setPrevFundBalance(null); setPrevConsumed(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, year, month]);

  // Load month's daily entries whenever school/month changes OR when any
  // Daily Entry is written anywhere in the app (dailyEntriesVersion bump).
  useEffect(() => {
    if (!schoolId) { setDailyEntries([]); return; }
    const from = `${year}-${String(month).padStart(2, "0")}-01`;
    const last = new Date(year, month, 0).getDate();
    const to = `${year}-${String(month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    let cancelled = false;
    dailyApi.list({ schoolId, fromDate: from, toDate: to })
      .then((d) => { if (!cancelled) setDailyEntries(d); })
      .catch(() => { if (!cancelled) setDailyEntries([]); });
    return () => { cancelled = true; };
  }, [schoolId, year, month, dailyEntriesVersion]);

  // Load saved MDCF (backend → fallback to localStorage), else auto-fill from daily entries.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. Try backend first
      let saved = null;
      if (schoolId) {
        try {
          saved = await mdcfApi.get(schoolId, year, month);
        } catch (err) {
          console.warn("MDCF backend load failed; using local cache", err);
        }
      }
      // 2. Fallback to localStorage cache
      if (!saved) saved = loadMdcf(schoolId, year, month);
      if (cancelled) return;
      if (saved && saved.data) {
        // iter113 — re-run autoFillMdcf on top of the saved doc so derived
        // identity (serialNo, totalChildren, category, state, enrolment)
        // and class-wise auto-computed sections (working days, etc.) stay
        // in sync with the latest school profile + daily entries every
        // time the page loads. Existing user-edited fields are preserved
        // because autoFillMdcf uses `||` fallback for editable identity
        // fields and overwrites only auto-computed sections.
        setData(autoFillMdcf({
          base: saved.data, school: activeWithFy, dailyEntries, cchs,
          fundBalance, prevFundBalance, prevConsumed, year, month,
        }));
      } else if (saved) {
        // older localStorage format stored the data object directly
        setData(autoFillMdcf({
          base: saved, school: activeWithFy, dailyEntries, cchs,
          fundBalance, prevFundBalance, prevConsumed, year, month,
        }));
      } else {
        setData(autoFillMdcf({ base: null, school: activeWithFy, dailyEntries, cchs, fundBalance, prevFundBalance, prevConsumed, year, month }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, year, month, dailyEntries, cchs, fundBalance, prevFundBalance, prevConsumed, activeWithFy, rateCardTick]);

  // Re-autofill helpers button
  const applyAutoFill = () => {
    setData((cur) => autoFillMdcf({ base: cur, school: activeWithFy, dailyEntries, cchs, fundBalance, prevFundBalance, prevConsumed, year, month }));
    toast.success("Auto-filled from Daily Entries + Active School");
  };

  const handleSave = async () => {
    if (!schoolId) { toast.error("Pick an active school first"); return; }
    // Always cache locally for offline use
    saveMdcf(schoolId, year, month, data);
    try {
      await mdcfApi.upsert({ schoolId, year, month, data });
      toast.success("MDCF saved to cloud");
    } catch (err) {
      console.error("MDCF save to cloud failed", err);
      toast.warning("Saved locally — cloud sync failed (will retry next time)");
    }
  };

  const handleReset = () => {
    setData(autoFillMdcf({ base: null, school: activeWithFy, dailyEntries, cchs, fundBalance, prevFundBalance, prevConsumed, year, month }));
    toast.success("Reset — auto-filled again");
  };

  const doPrint = (which) => {
    // Use body class to mark which section to print; CSS hides the rest.
    document.body.classList.add(`mdcf-print-${which}`);
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove(`mdcf-print-${which}`), 200);
    }, 80);
  };

  if (profiles.length === 0) {
    return (
      <main className="min-h-screen bg-sand-50 text-ink-900">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">Setup needed</p>
          <h1 className="mt-3 font-heading text-3xl font-semibold">Add a school first</h1>
          <p className="mt-3 text-ink-700">The MDCF needs an active school. Open <Link to="/setup" className="font-semibold text-clay underline">Setup</Link> to add one.</p>
        </div>
      </main>
    );
  }

  // Shallow update helpers
  const set = (path, value) => {
    setData((cur) => {
      const clone = JSON.parse(JSON.stringify(cur));
      let node = clone;
      const parts = path.split(".");
      parts.slice(0, -1).forEach((p) => { node = node[p]; });
      node[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const general = data.general;

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />

      {/* Mobile-only (<md) compact layout. */}
      <MobileMonthlyMDCF
        active={active}
        year={year} month={month}
        setYear={setYear} setMonth={setMonth}
        yearChoices={yearChoices}
        data={data}
        set={set}
        dailyEntries={dailyEntries}
        applyAutoFill={applyAutoFill}
        handleSave={handleSave}
        handleReset={handleReset}
        doPrint={doPrint}
      />

      <div className="hidden md:block">
      {/* Header */}
      <div className="no-print mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">Monthly MDCF</p>
        <h1 className="mt-2 font-heading text-4xl font-semibold tracking-tight sm:text-5xl">
          Monthly Data Capture Format
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink-700">
          The official Odia MDM monthly bill and English PM POSHAN MDCF for <b>{active?.schoolName}</b> (UDISE {active?.udiseCode}).
          Auto-filled from your Active School and saved Daily Entries.
        </p>
      </div>

      {/* Controls */}
      <section className="no-print mx-auto mt-6 max-w-7xl rounded-xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-6 mx-4 sm:mx-6 lg:mx-8" data-testid="mdcf-controls">
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Year</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="mdcf-year-select" className="h-11 w-32 bg-white border-border"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Month</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="mdcf-month-select" className="h-11 w-44 bg-white border-border"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_OPTIONS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.en} / {m.odia}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>

          <div className="ml-auto flex flex-wrap gap-2">
            <Button onClick={applyAutoFill} variant="outline" size="sm" data-testid="mdcf-autofill-btn" className="gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Re-autofill
            </Button>
            <Button onClick={handleSave} size="sm" data-testid="mdcf-save-btn" className="gap-2 bg-clay text-white hover:bg-clay-dark">
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
            <Button onClick={handleReset} variant="outline" size="sm" data-testid="mdcf-reset-btn" className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" /> Reset
            </Button>
            <Button onClick={() => doPrint("odia")} size="sm" data-testid="mdcf-print-odia-btn" className="gap-2 bg-ink-900 text-white hover:bg-ink-700">
              <Printer className="h-3.5 w-3.5" /> Print Odia
            </Button>
            <Button onClick={() => doPrint("odia2")} size="sm" data-testid="mdcf-print-odia2-btn" className="gap-2 bg-clay text-white hover:bg-clay-dark">
              <Printer className="h-3.5 w-3.5" /> Print Odia Format 2
            </Button>
            <Button onClick={() => doPrint("eng")} size="sm" data-testid="mdcf-print-eng-btn" className="gap-2 bg-ink-900 text-white hover:bg-ink-700">
              <Printer className="h-3.5 w-3.5" /> Print English
            </Button>
          </div>
        </div>

        <div className="mt-3 rounded-lg bg-moss/5 px-3 py-2 text-xs text-moss-dark" data-testid="mdcf-autofill-hint">
          Loaded <b>{dailyEntries.length}</b> daily {dailyEntries.length === 1 ? "entry" : "entries"} for {MONTH_OPTIONS.find((m) => m.v === month)?.en} {year}.
        </div>
      </section>

      {/* Mini-editor for the commonly-edited fields (general + working days) */}
      <section className="no-print mx-auto mt-6 max-w-7xl rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8 mx-4 sm:mx-6 lg:mx-8">
        <h3 className="mb-4 font-heading text-lg font-semibold">Quick Edit — General</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField testId="mdcf-school-serial" label="School Serial No." value={general.schoolSerialNo} onChange={(v) => set("general.schoolSerialNo", v)} />
          <TextField testId="mdcf-total-children" label="Total Children" type="number" value={general.totalChildren} onChange={(v) => set("general.totalChildren", v)} />
          <TextField testId="mdcf-kitchen-type" label="Kitchen Type" value={general.kitchenType} onChange={(v) => set("general.kitchenType", v)} />
          <SelectField
            testId="mdcf-school-type"
            label="Type of School (English MDCF)"
            value={general.schoolType || ""}
            onChange={(v) => set("general.schoolType", v)}
            options={[
              { value: "", label: "—" },
              { value: "Government", label: "Government" },
              { value: "Local Body", label: "Local Body" },
              { value: "EGS/AIE Centers", label: "EGS/AIE Centers" },
              { value: "NCLP", label: "NCLP" },
              { value: "Madras / Maqtab", label: "Madras / Maqtab" },
            ]}
          />
          <SelectField
            testId="mdcf-school-category"
            label="Category (English MDCF)"
            value={general.category || ""}
            onChange={(v) => set("general.category", v)}
            options={[
              { value: "", label: "—" },
              { value: "Primary", label: "Primary" },
              { value: "Upper Primary", label: "Upper Primary" },
              { value: "Primary with Upper Primary", label: "Primary with Upper Primary" },
            ]}
          />
          <SelectField
            testId="mdcf-inspection-done"
            label="School Inspection done during the month"
            value={data.eng.inspection.inspectionDone || ""}
            onChange={(v) => set("eng.inspection.inspectionDone", v)}
            options={[
              { value: "", label: "—" },
              { value: "Yes", label: "Yes" },
              { value: "No", label: "No" },
            ]}
          />
          <TextField testId="mdcf-ngo-shg" label="NGO / SHG" value={general.ngoShg} onChange={(v) => set("general.ngoShg", v)} />
          <TextField testId="mdcf-state" label="State / UT" value={general.stateUT} onChange={(v) => set("general.stateUT", v)} />
          <TextField testId="mdcf-enrolment" label="Enrolment (total)" type="number" value={general.enrolment} onChange={(v) => set("general.enrolment", v)} />
        </div>

        <h3 className="mt-8 mb-4 font-heading text-lg font-semibold">Working Days (Section 3) — classwise</h3>
        <div className="grid gap-4 sm:grid-cols-4">
          <TextField label="Bal Vatika" type="number" value={data.monthly.section2.totalWorkingDays.balBatika} onChange={(v) => set("monthly.section2.totalWorkingDays.balBatika", v)} />
          <TextField label="Primary" type="number" value={data.monthly.section2.totalWorkingDays.primaryClass} onChange={(v) => set("monthly.section2.totalWorkingDays.primaryClass", v)} />
          <TextField label="Upper Primary" type="number" value={data.monthly.section2.totalWorkingDays.upperPrimaryClass} onChange={(v) => set("monthly.section2.totalWorkingDays.upperPrimaryClass", v)} />
          <TextField label="High School" type="number" value={data.monthly.section2.totalWorkingDays.upperSchool} onChange={(v) => set("monthly.section2.totalWorkingDays.upperSchool", v)} />
        </div>

        <h3 className="mt-8 mb-4 font-heading text-lg font-semibold">English MDCF — Health &amp; Inspection (manual)</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField label="IFA tablets (Boys)" type="number" value={data.eng.healthStatus.ifaBoys} onChange={(v) => set("eng.healthStatus.ifaBoys", v)} />
          <TextField label="IFA tablets (Girls)" type="number" value={data.eng.healthStatus.ifaGirls} onChange={(v) => set("eng.healthStatus.ifaGirls", v)} />
          <TextField label="RBSK Screened" type="number" value={data.eng.healthStatus.rbskScreened} onChange={(v) => set("eng.healthStatus.rbskScreened", v)} />
          <TextField label="RBSK Referred" type="number" value={data.eng.healthStatus.rbskReferred} onChange={(v) => set("eng.healthStatus.rbskReferred", v)} />
          <TextField label="Inspections by Task Force" type="number" value={data.eng.inspection.byTaskForce} onChange={(v) => set("eng.inspection.byTaskForce", v)} />
          <TextField label="By District Officials" type="number" value={data.eng.inspection.byDistrictOfficials} onChange={(v) => set("eng.inspection.byDistrictOfficials", v)} />
          <TextField label="By Block Officials" type="number" value={data.eng.inspection.byBlockOfficials} onChange={(v) => set("eng.inspection.byBlockOfficials", v)} />
          <TextField label="By SMC Members" type="number" value={data.eng.inspection.bySMCMembers} onChange={(v) => set("eng.inspection.bySMCMembers", v)} />
        </div>
      </section>

      {/* Report Preview */}
      <div className="no-print mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="mdcf-tab-odia"
            onClick={() => setTab("odia")}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
              tab === "odia" ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-900 hover:border-clay hover:text-clay"
            }`}
          >
            Odia MDM Bill
          </button>
          <button
            data-testid="mdcf-tab-odia2"
            onClick={() => setTab("odia2")}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
              tab === "odia2" ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-900 hover:border-clay hover:text-clay"
            }`}
          >
            Odia MDM Bill Format 2
          </button>
          <button
            data-testid="mdcf-tab-eng"
            onClick={() => setTab("eng")}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
              tab === "eng" ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-900 hover:border-clay hover:text-clay"
            }`}
          >
            English MDCF
          </button>
        </div>
      </div>

      </div>
      {/* End desktop-only block. */}

      {/* Report previews — render on BOTH mobile and desktop so phones can
          view & print the official MDM bills. Tab switching happens via the
          desktop pills above OR the mobile tab pills inside MobileMonthlyMDCF. */}
      <div className="mx-auto mt-6 max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        {/* Mobile-only tab pills (desktop pills are above inside the wrapper). */}
        <div className="mb-4 flex gap-2 md:hidden" data-testid="m-mdcf-tabs">
          <button
            type="button"
            data-testid="m-mdcf-tab-odia"
            onClick={() => setTab("odia")}
            className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
              tab === "odia" ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-900"
            }`}
          >
            Odia MDM
          </button>
          <button
            type="button"
            data-testid="m-mdcf-tab-odia2"
            onClick={() => setTab("odia2")}
            className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
              tab === "odia2" ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-900"
            }`}
          >
            Format 2
          </button>
          <button
            type="button"
            data-testid="m-mdcf-tab-eng"
            onClick={() => setTab("eng")}
            className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
              tab === "eng" ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-900"
            }`}
          >
            English MDCF
          </button>
        </div>
        <div className="overflow-x-auto">
          <div id="mdcf-odia-container" className={tab === "odia" ? "" : "hidden"}>
            <OdiaMdmReport data={data} />
          </div>
          <div id="mdcf-odia2-container" className={tab === "odia2" ? "" : "hidden"}>
            <OdiaMdmReportSimple data={data} />
          </div>
          <div id="mdcf-eng-container" className={tab === "eng" ? "" : "hidden"}>
            <EngMdcfReport data={data} />
          </div>
        </div>
      </div>
    </main>
  );
}
